import {
  ApiRoute,
  CandidateProfile,
  CandidateProfileFull,
  CandidateProfileFullSchema,
  CandidateProfileSchema,
  CandidateSection,
  CreateUserProfile,
  ProfileDetail,
  ProfileDetailSchema,
  SECTION_SCHEMA_MAP,
  UpdateCandidateProfile,
  UpdateUserProfile,
  User,
  UserSchema,
  getApiRoute
} from "@humanex/interfaces";
import { z } from "zod";

import { ApiClientConfig, BaseApiClient } from "./base-client";

export const LS_REFERRAL_CODE_KEY = "ref_code";

export class ProfileApiClient extends BaseApiClient {
  constructor(config: ApiClientConfig) {
    super({
      ...config,
      baseEndpoint: getApiRoute(ApiRoute.PROFILE),
      requireAuth: true
    });
  }

  async getMyProfile(): Promise<ProfileDetail | null> {
    const referral = localStorage.getItem(LS_REFERRAL_CODE_KEY);
    return await this.get("me", ProfileDetailSchema, {
      params: { referral },
      requireAuth: true
    });
  }

  async getProfileByUserId(userId: string): Promise<ProfileDetail | null> {
    return await this.get(userId, ProfileDetailSchema, {
      requireAuth: false
    });
  }

  // Convenience methods for current user (recommended)
  async createMyProfile(profileData: CreateUserProfile): Promise<User> {
    return await this.post("me", UserSchema, profileData, {
      requireAuth: true
    });
  }

  async updateMyProfile(updateData: UpdateUserProfile): Promise<ProfileDetail> {
    return await this.put("me", ProfileDetailSchema, updateData, {
      requireAuth: true
    });
  }

  // async deleteMyProfile(): Promise<void> {
  //   return await this.delete("me", z.void(), { requireAuth: true });
  // }

  async setAvatar(avatarUrl: string, key?: string): Promise<ProfileDetail> {
    return this.patch("me/avatar", ProfileDetailSchema, { avatar: avatarUrl, key });
  }

  async updateCandidateProfile(data: UpdateCandidateProfile): Promise<CandidateProfile> {
    return this.put("me/candidate", CandidateProfileSchema, data, {
      requireAuth: true
    });
  }

  async getMyCandidateProfile(): Promise<CandidateProfileFull> {
    return this.get("me/candidate", CandidateProfileFullSchema, {
      requireAuth: true
    });
  }

  async getCandidateProfileByUserId(userId: string): Promise<CandidateProfileFull> {
    return this.get(`${userId}/candidate`, CandidateProfileFullSchema, {
      requireAuth: true
    });
  }

  async updateCandidateSection<S extends CandidateSection>(
    section: S,
    data: z.infer<(typeof SECTION_SCHEMA_MAP)[S]>
  ): Promise<CandidateProfileFull> {
    return this.put(`me/candidate/${section}`, CandidateProfileFullSchema, data, { requireAuth: true });
  }
}
